import { eventsClient } from '@/clients/events-client.js';

const SSE_DURATION_MS = 90_000;
const RECONNECT_THRESHOLD_MS = 20_000;
const MAX_SESSION_MS = 120_000; // 2 minutes total before giving up

class SseService {
    private _source: EventSource | null = null;
    private _consumers = new Set<string>();
    private _listeners = new Map<string, Map<string, (data: any) => void>>();
    private _openedAt: number | null = null;
    private _sessionStartedAt: number | null = null;
    private _timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    private _connecting = false;

    subscribe(consumerId: string, eventType: string, callback: (data: any) => void) {
        this._consumers.add(consumerId);

        if (!this._listeners.has(eventType)) {
            this._listeners.set(eventType, new Map());
        }
        this._listeners.get(eventType)!.set(consumerId, callback);

        this._ensureConnected();
    }

    unsubscribe(consumerId: string) {
        this._consumers.delete(consumerId);

        for (const callbacks of this._listeners.values()) {
            callbacks.delete(consumerId);
        }

        if (this._consumers.size === 0) {
            this._sessionStartedAt = null;
            this._close();
        }
    }

    async _ensureConnected() {
        if (this._source) {
            const elapsed = Date.now() - this._openedAt!;
            const remaining = SSE_DURATION_MS - elapsed;

            if (remaining > RECONNECT_THRESHOLD_MS) {
                return;
            }

            console.log(`[SSE] only ${Math.round(remaining / 1000)}s remaining, reconnecting`);
            this._close();
        }

        await this._connect();
    }

    _withinSession(): boolean {
        if (!this._sessionStartedAt) return true;
        return Date.now() - this._sessionStartedAt < MAX_SESSION_MS;
    }

    async _connect() {
        if (this._connecting) return;

        if (!this._withinSession()) {
            console.log('[SSE] 2-minute session limit reached, giving up');
            return;
        }

        this._connecting = true;
        if (!this._sessionStartedAt) this._sessionStartedAt = Date.now();

        try {
            const { token } = await eventsClient.issueToken();

            this._source = new EventSource(eventsClient.eventStreamUrl(token));
            this._openedAt = Date.now();

            this._source.addEventListener('connected', () => {
                console.log('[SSE] connection established');
            });

            this._source.addEventListener('refreshRequest', (e) => {
                console.log('[SSE] refreshRequest received raw', e.data);
                this._dispatch('refreshRequest', JSON.parse(e.data));
            });

            this._source.addEventListener('timeout', () => {
                console.log('[SSE] server closed connection (timeout)');
                this._close();
                if (this._consumers.size > 0 && this._withinSession()) this._connect();
            });

            this._source.onerror = (err) => {
                console.error('[SSE] connection error', err);
                this._close();
                if (this._consumers.size > 0 && this._withinSession()) setTimeout(() => this._connect(), 3000);
            };

            this._timeoutHandle = setTimeout(() => {
                console.log('[SSE] client-side timeout, closing');
                this._close();
                if (this._consumers.size > 0 && this._withinSession()) this._connect();
            }, SSE_DURATION_MS + 5000);

        } catch (err) {
            console.error('[SSE] failed to connect', err);
        } finally {
            this._connecting = false;
        }
    }

    _close() {
        if (this._timeoutHandle) {
            clearTimeout(this._timeoutHandle);
            this._timeoutHandle = null;
        }
        if (this._source) {
            this._source.close();
            this._source = null;
            this._openedAt = null;
        }
    }

    _dispatch(eventType: string, data: any) {
        const callbacks = this._listeners.get(eventType);
        if (!callbacks) return;
        for (const cb of callbacks.values()) {
            try {
                cb(data);
            } catch (err) {
                console.error(`[SSE] error in listener for ${eventType}`, err);
            }
        }
    }
}

export const sseService = new SseService();